import { Injectable } from '@angular/core';
import { getItemAsJson, setItemAsJson } from 'helpers';
import { logging, TrackProcess } from 'helpers/monitoring';
import * as _ from 'lodash';
import { Branch, OrderedItem, OrderedItemType, SendOrderParameters } from 'models';
import { BehaviorSubject, combineLatest, EMPTY, from, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    finalize,
    map,
    pairwise,
    skipWhile,
    switchMap,
    take,
    tap
} from 'rxjs/operators';
import { SegmentService } from 'services';

import { Header, Row } from 'app/b2b-order/components';
import { CartErrors } from 'app/b2b-order/components/shopping-cart/shopping-cart.component';
import {
    calculateTotalAmountMapPerArticle,
    containsInsufficientQuantityError,
    containsInvalidOrderQuantityIncrementError,
    containsStoreWithoutAccessError,
    getMatrixSelectedAmount,
    getStoreSelectedAmount,
    getTotalPriceByItem
} from 'app/b2b-order/helpers/order';
import { formatPrice } from 'app/b2b-order/helpers/price';
import {
    calculateItemsTotalPrice,
    getItemFromArticleWithKey,
    getItemIdentifier
} from 'app/b2b-order/helpers/shopping-cart';
import { DefaultSelectionType, ReorderArticle, ReorderArticleSize, ReorderPrepack } from 'app/b2b-order/types/reorder';
import { MinimumOrderRestriction, OrderRestrictionType } from 'services';
import { TrackingEvents } from 'services/tracking/gtm';
import { UserService } from 'services/user.service';
import { OrderService } from '../order.service';
import { StoreSelectorService } from '../store-selector';
import { StoreService } from '../store.service';
import { BrandsOrderRestriction, ShoppingCartItem } from './types';

const manufacturerShoppingCartItemsStorageKey = 'manufacturerShoppingCartItems1';
const retailerShoppingCartItemsStorageKey = 'shoppingCartItems1';
const lastUserOrganizationStorageKey = 'lastUserOrganization';

@Injectable({ providedIn: 'root' })
export class ShoppingCartService {
    // Errors and Warnings
    public isOrderValid: Observable<boolean>;
    public errorMessages: BehaviorSubject<StoreErrorMessage[] | null>;

    public isSingleStore = new BehaviorSubject(true);
    // Items data
    public items: BehaviorSubject<ShoppingCartItem[]> = new BehaviorSubject<ShoppingCartItem[]>([]);
    public count: Observable<number>; // amount of grouped items
    public amountOfNewArticles: Subject<number> = new Subject();
    public sourceStoreIdForCopying: Subject<string> = new Subject();
    public targetStoreIdsForCopying: Subject<string[]> = new Subject();
    private listOfItems: ShoppingCartList;
    // Store state
    public isProcessingOrder: Observable<boolean>;
    public itemsTotalAmount: Observable<number>;
    public itemsTotalPrice: Observable<string>;
    private isOrderBeingPlaced: BehaviorSubject<boolean>;
    // Inner info
    private storageKey: string;

    public subscriptions = new Subscription();
    itemCountByStore: Observable<Record<string, number>>;

    constructor(
        private orderService: OrderService,
        private userService: UserService,
        private segmentService: SegmentService,
        private storeService: StoreService,
        private storeSelectorService: StoreSelectorService
    ) {
        this.storageKey = this.userService.isManufacturer()
            ? manufacturerShoppingCartItemsStorageKey
            : retailerShoppingCartItemsStorageKey;

        this.listOfItems = getItemAsJson(this.storageKey) || {};

        this.items.next(_.values(this.listOfItems));
        this.count = this.items.pipe(map((items) => _.size(_.keys(items))));

        this.itemsTotalAmount = this.items.pipe(
            map((items) =>
                _.reduce(items, (sum, item) => sum + _.sum(_.values(calculateTotalAmountMapPerArticle(item))), 0)
            )
        );
        this.subscriptions.add(
            this.storeSelectorService.selectedStores
                .pipe(map((selectedStores) => selectedStores?.length < 2))
                .subscribe((isSingleStore) => this.isSingleStore.next(isSingleStore))
        );

        this.subscriptions.add(
            this.storeSelectorService.selectedStores
                .pipe(
                    pairwise(),
                    tap(([prevSelectedStores, selectedStores]) =>
                        this.updateItemsByStore(selectedStores, prevSelectedStores)
                    )
                )
                .subscribe()
        );

        this.itemsTotalPrice = this.items.pipe(
            map((items) => {
                if (_.isEmpty(items)) {
                    return '--';
                }
                const itemsCurrency = _.toUpper(items[0].price.currency);
                const totalPrice = calculateItemsTotalPrice(items, itemsCurrency);

                return totalPrice === -1
                    ? '--'
                    : formatPrice(totalPrice, itemsCurrency, this.userService.getPreferredLanguage());
            })
        );
        this.errorMessages = new BehaviorSubject(null);
        this.isOrderBeingPlaced = new BehaviorSubject(false);

        this.isOrderValid = combineLatest([this.items, this.storeSelectorService.selectedStores]).pipe(
            map(([items, stores]) => {
                const isOrderValid = this.hasValidItemsForOrder(items, stores);

                if (stores.length && isOrderValid) {
                    this.errorMessages.next(null);
                }

                return isOrderValid;
            })
        );

        this.itemCountByStore = this.items.pipe(
            map((items) =>
                items.reduce((acc, item) => {
                    const alreadyCountedStoresMap = {};
                    item.sizes.forEach((size) => {
                        const amount = size.stock.selectedAmount;
                        if (typeof amount === 'number' && amount > 0) {
                            acc[item.storeId]++;
                        }
                        Object.entries(size.stock.selectedAmount).forEach(([storeId, amount]) => {
                            if (!alreadyCountedStoresMap[storeId] && amount > 0) {
                                acc[storeId] = (acc[storeId] ?? 0) + 1;
                                alreadyCountedStoresMap[storeId] = true;
                            }
                        });
                    });

                    return acc;
                }, {})
            )
        );
    }

    public sendOrders(userData: SendOrderParameters['userData'], additionalData: object) {
        const itemsByStore = this.groupItemsByStore();
        const storesIds = Object.keys(itemsByStore);

        if (this.isOrderBeingPlaced.getValue()) {
            return throwError(new Error('OrderBeingPlaced'));
        }

        this.isOrderBeingPlaced.next(true);
        this.errorMessages.next([]);
        const startTime = performance.now();
        return this.storeSelectorService.selectedStores.pipe(
            skipWhile((stores) => stores.length === 0),
            take(1),
            concatMap((storesArray) => storesArray),
            filter((store) => storesIds.includes(store._id)),
            switchMap(
                // Transform the observable from array of store to OrderResponse
                (store, index) =>
                    from(
                        this.sendOrder({
                            store: {
                                ...store,
                                name: store.name || userData.organizationName
                            },
                            orderedItems: itemsByStore[store._id],
                            userData,
                            ...additionalData
                        })
                    ).pipe(
                        catchError((err) => {
                            console.error(
                                `Multi-store Order failed for store with id: ${store._id}, order position: ${index}`,
                                err
                            );
                            return EMPTY;
                        })
                    )
            ),
            finalize(() => {
                const endTime = performance.now();
                const elapsedTimeMS = endTime - startTime;
                logging.info(`Multi-store order completed!`, { orderCount: storesIds.length, elapsedTimeMS });
                this.isOrderBeingPlaced.next(false);
            })
        );
    }

    @TrackProcess('Order')
    public sendOrder(sendOrderData: SendOrderParameters): Promise<{ orderNumber: string }> {
        const isMultiStore = !_.isEmpty(sendOrderData.orderedItems);
        const stores = this.storeService.stores.getValue();
        const isMultiStoreUserWithSingleStoreOrder = stores.length > 1 && !isMultiStore;

        if (!isMultiStore) {
            if (this.isOrderBeingPlaced.getValue()) {
                return Promise.reject(new Error('OrderBeingPlaced'));
            }
            this.isOrderBeingPlaced.next(true);
        }

        const orderData = { ...sendOrderData, orderedItems: sendOrderData.orderedItems ?? this.getItemsToOrder() };
        orderData.orderedItems = orderData.orderedItems.filter((item) => !!item.quantity);
        if (!orderData.orderedItems.length) {
            return;
        }

        return this.orderService
            .createOrder(orderData)
            .then((order) => {
                try {
                    this.trackOrder(order, isMultiStore, isMultiStoreUserWithSingleStoreOrder);
                } catch (error) {
                    console.error(error);
                }
                if (isMultiStore) {
                    this.removeItemsByStoreId(sendOrderData.store._id);
                } else {
                    this.isOrderBeingPlaced.next(false);
                    this.clear();
                }

                return order;
            })
            .catch((error) => {
                let realError = error;
                if (typeof error === 'function') {
                    try {
                        realError = error();
                    } catch (e) {
                        realError = error;
                    }
                }
                const errorsToAdd: StoreErrorMessage[] = this.errorMessages.getValue() ?? [];
                if (containsInsufficientQuantityError(realError.data)) {
                    realError.data.errors.forEach((currentError) => {
                        const gtin = currentError.gtin;
                        const availableQuantity = currentError.availableQuantity;

                        this.updateAvailableStock(gtin, availableQuantity, sendOrderData.store);
                    });
                    errorsToAdd.push({
                        message: StoreErrors.InsufficientQuantity,
                        data: {
                            articles: realError.data.errors,
                            store: sendOrderData.store
                        }
                    });
                } else if (containsStoreWithoutAccessError(realError.data)) {
                    errorsToAdd.push({
                        message: StoreErrors.StoreHasNoAccessTo,
                        data: {
                            brands: this.getBrandsStoreHasNoAccessTo(realError.data.errors),
                            store: sendOrderData.store
                        }
                    });
                } else if (containsInvalidOrderQuantityIncrementError(realError.data)) {
                    const filteredOrderIncrementErrors = realError.data.errors.filter(
                        (error) => error.type === 'InvalidOrderQuantityIncrement'
                    );
                    const filteredOrderIncrementErrorsData = filteredOrderIncrementErrors.map((error) => ({
                        message: StoreErrors.InvalidOrderQuantityIncrement,
                        data: { realError, store: sendOrderData.store }
                    }));
                    for (const error of filteredOrderIncrementErrorsData) {
                        this.updateOrderIncrement(realError.data.error.gtin, realError.data.error.increment);
                        errorsToAdd.push(error);
                    }
                } else {
                    errorsToAdd.push({
                        message: StoreErrors.Unknown,
                        data: {
                            error: realError,
                            store: sendOrderData.store
                        }
                    });
                }
                this.errorMessages.next(errorsToAdd);

                if (!isMultiStore) {
                    this.isOrderBeingPlaced.next(false);
                }
                return Promise.reject(realError);
            });
    }

    public add(items: ShoppingCartItem[]): void {
        let itemsToAdd = _.cloneDeep(items);
        if (items.every((item) => item.storeId)) {
            itemsToAdd = items.map((article) => {
                const selectedArticle = this.listOfItems[getItemIdentifier(article)];

                article.sizes = article.sizes.map(
                    (size) =>
                        this.updateSelectedQuantities(
                            selectedArticle,
                            article.storeId,
                            size,
                            false
                        ) as ReorderArticleSize
                );

                article.prepacks = article.prepacks.map(
                    (prepack) =>
                        this.updateSelectedQuantities(selectedArticle, article.storeId, prepack, true) as ReorderPrepack
                );

                return article;
            });
        }
        _.forEach(itemsToAdd, (item: ShoppingCartItem) => (this.listOfItems[getItemIdentifier(item)] = item));
        this.amountOfNewArticles.next(_.size(itemsToAdd));
        this.changed();
    }

    public removeSelectedSizes<T extends ReorderArticleSize | ReorderPrepack>(sizes: Array<T>, storeId: string) {
        const filteredSizes = _.cloneDeep(sizes);

        filteredSizes.forEach((size) => {
            if (size.stock.selectedAmount.hasOwnProperty(storeId)) {
                delete size.stock.selectedAmount[storeId];
            }
        });

        return filteredSizes;
    }

    public remove(item: ShoppingCartItem): void {
        const itemToRemove = this.listOfItems[getItemIdentifier(item)];

        if (item.storeId && !this.isSingleStore.getValue()) {
            itemToRemove.sizes = this.removeSelectedSizes(itemToRemove.sizes, item.storeId);
            itemToRemove.prepacks = this.removeSelectedSizes(itemToRemove.prepacks, item.storeId);

            const hasSelectedAmount = (
                size: ReorderArticleSize<DefaultSelectionType> | ReorderPrepack<DefaultSelectionType>
            ) => {
                if (typeof size.stock.selectedAmount === 'number' && size.stock.selectedAmount) {
                    return true;
                }

                return (
                    !_.isEmpty(size.stock.selectedAmount) &&
                    Object.values(size.stock.selectedAmount).some((amount) => amount)
                );
            };

            const hasNoSizes = itemToRemove.sizes.every((size) => !hasSelectedAmount(size));
            const hasNoPrepacks = itemToRemove.prepacks.every((prepack) => !hasSelectedAmount(prepack));

            if (hasNoSizes && hasNoPrepacks) {
                this.listOfItems = _.omit(this.listOfItems, getItemIdentifier(item));
            }
        } else {
            this.listOfItems = _.omit(this.listOfItems, getItemIdentifier(item));
        }

        this.changed();

        this.segmentService.track(TrackingEvents.REMOVE_FROM_CART, {
            ecommerce: {
                value: item.price?.purchasePrice ? getTotalPriceByItem(item) : null,
                items: _.map(item.sizes, (size) => ({
                    item_id: size.gtin,
                    item_name: size.name,
                    affiliation: 'reorder',
                    currency: size.price?.currency,
                    discount: size.price?.discount,
                    item_brand: size.brandName,
                    item_list_id: 'related_products',
                    item_list_name: 'Related Products',
                    item_variant: item?.name,
                    price: size.price?.discountedPurchasePrice || size.price?.purchasePrice,
                    quantity: size.stock.selectedAmount
                }))
            }
        });
    }

    public groupItemsByStore(): { [storeId: string]: OrderedItem[] } {
        const items = this.getItemsToOrder();
        return items.reduce((acc, item) => {
            const { quantity } = item;
            _.entries(typeof quantity === 'object' ? quantity : {}).forEach(([storeId, value]) => {
                if (!acc[storeId]) {
                    acc[storeId] = [];
                }
                acc[storeId].push({ ...item, quantity: value });
            });
            return acc;
        }, {});
    }

    public removeItemsByStoreId(storeId: string) {
        const removedArticles = [];

        Object.entries(this.listOfItems).forEach(([id, item]) => {
            item.sizes = this.removeSelectedSizes(item.sizes, storeId);
            item.prepacks = this.removeSelectedSizes(item.prepacks, storeId);

            const hasSelectedAmount = (
                size: ReorderArticleSize<DefaultSelectionType> | ReorderPrepack<DefaultSelectionType>
            ) => {
                if (typeof size.stock.selectedAmount === 'number' && size.stock.selectedAmount) {
                    return true;
                }

                return (
                    !_.isEmpty(size.stock.selectedAmount) &&
                    Object.values(size.stock.selectedAmount).some((amount) => amount)
                );
            };

            const hasNoSizes = item.sizes.every((size) => !hasSelectedAmount(size));
            const hasNoPrepacks = item.prepacks.every((prepack) => !hasSelectedAmount(prepack));

            if (hasNoSizes && hasNoPrepacks) {
                removedArticles.push(id);
            }
        });

        removedArticles.forEach((id) => {
            delete this.listOfItems[id];
        });

        this.changed();
    }

    public clear(isUserAction = false): void {
        if (isUserAction) {
            this.trackClearCart();
        }
        this.listOfItems = {};
        this.errorMessages.next(null);
        this.changed();
    }

    public cartSanitization() {
        const lastUserOrganizationId = getItemAsJson(lastUserOrganizationStorageKey);
        const userOrganizationId = this.userService.getOrganizationId();
        if (lastUserOrganizationId && lastUserOrganizationId !== userOrganizationId) {
            this.listOfItems = {};
            this.storeSelectorService.clearSelectedStores();
        }
        this.errorMessages.next(null);
        setItemAsJson(lastUserOrganizationStorageKey, null);
        this.changed();
    }

    public getItemsObservable() {
        return this.items.asObservable();
    }

    public getValidShoppingCartItemsFromReorderArticles(articles: ReorderArticle[]): ShoppingCartItem[] {
        return _.reduce(
            articles,
            (cartItems: ShoppingCartItem[], article) => {
                const orderedSizes = _.filter(article.sizes, ({ stock }) => Boolean(stock.selectedAmount));
                const orderedPrepacks = _.filter(article.prepacks, ({ stock }) => Boolean(stock.selectedAmount));
                const hasOrderedPrepacks = !_.isEmpty(orderedPrepacks);
                const hasOrderedSizes = !_.isEmpty(orderedSizes);

                return [
                    ...cartItems,
                    ...(hasOrderedPrepacks || hasOrderedSizes
                        ? [
                              {
                                  ...article,
                                  prepacks: hasOrderedPrepacks ? orderedPrepacks : [],
                                  sizes: orderedSizes
                              }
                          ]
                        : [])
                ];
            },
            []
        );
    }

    public checkBelowMinimumOrderRestriction(
        articles: ShoppingCartItem[],
        minimumOrderRestriction: MinimumOrderRestriction,
        selectedStores: Branch[]
    ) {
        if (this.isSingleStore.getValue()) {
            return this.checkBelowMinimumOrderRestrictionPerStore(articles, minimumOrderRestriction);
        }

        return selectedStores.some((store) =>
            this.checkBelowMinimumOrderRestrictionPerStore(articles, minimumOrderRestriction, store._id)
        );
    }

    public checkBelowMinimumOrderRestrictionPerStore(
        articles: ShoppingCartItem[],
        minimumOrderRestriction?: MinimumOrderRestriction,
        storeId?: string
    ) {
        if (!minimumOrderRestriction) {
            return false;
        }

        const usesOrderVolumeRestriction = minimumOrderRestriction.type === OrderRestrictionType.OrderVolume;

        if (usesOrderVolumeRestriction) {
            const itemsCurrency = _.toUpper(articles[0].price.currency);
            const minimumOrderValueByCurrency = minimumOrderRestriction.parameter;

            if (!minimumOrderValueByCurrency || !minimumOrderValueByCurrency[itemsCurrency]) {
                return false;
            }

            const totalPrice = calculateItemsTotalPrice(articles, itemsCurrency, storeId);
            return totalPrice !== -1 && totalPrice < minimumOrderValueByCurrency[itemsCurrency];
        }

        const amountOfItemsToOrder = _.sumBy(
            articles,
            (article) =>
                _.sumBy(article.sizes, (size) => getStoreSelectedAmount(size.stock.selectedAmount, storeId)) +
                _.sumBy(article.prepacks, (prepack) =>
                    _.sumBy(
                        prepack.products,
                        (product) => getStoreSelectedAmount(prepack.stock.selectedAmount, storeId) * product.quantity
                    )
                )
        );
        return amountOfItemsToOrder < (minimumOrderRestriction.parameter as number);
    }

    public isBelowMinimumOrderRestriction(
        articles: ShoppingCartItem[],
        orderRestrictionByBrand: BrandsOrderRestriction,
        brand: string,
        stores: Branch[]
    ): boolean {
        const minimumOrderRestriction = this.getMinimumOrderRestriction(orderRestrictionByBrand, brand);
        if (!minimumOrderRestriction) {
            return false;
        }
        return this.checkBelowMinimumOrderRestriction(articles, minimumOrderRestriction, stores);
    }

    public getMinimumOrderRestrictionType(minimumOrderRestrictionByBrand: BrandsOrderRestriction, brand: string) {
        const minimumOrderRestriction = this.getMinimumOrderRestriction(minimumOrderRestrictionByBrand, brand);
        if (!minimumOrderRestriction) {
            return null;
        }

        return minimumOrderRestriction.type;
    }

    public getRestrictionValueByBrand(
        items: ShoppingCartItem[],
        minimumOrderRestrictionByBrand: BrandsOrderRestriction,
        brandName: string
    ): string {
        const usesOrderVolumeRestriction =
            this.getMinimumOrderRestrictionType(minimumOrderRestrictionByBrand, brandName) ===
            OrderRestrictionType.OrderVolume;

        if (usesOrderVolumeRestriction) {
            const currency = _.toUpper(items[0].price.currency);
            const minOrderVolume = minimumOrderRestrictionByBrand[brandName].parameter[currency];
            return formatPrice(minOrderVolume, currency, this.userService.getPreferredLanguage());
        }

        const parameter = _.get(minimumOrderRestrictionByBrand, `[${brandName}].parameter`);
        return _.toString(parameter);
    }

    public updateItemsByStore(stores: Branch[], previousStores: Branch[]) {
        const newStoreSelection = stores.map(({ _id }) => _id);
        const previousStoreSelection = (previousStores || []).map(({ _id }) => _id);

        const storesToRemove = previousStoreSelection.filter((store) => !newStoreSelection.includes(store));
        storesToRemove.forEach((id) => this.removeItemsByStoreId(id));

        if (newStoreSelection.length > 1 && previousStores.length !== 0) {
            if (previousStoreSelection.length === 1) {
                if (newStoreSelection.includes(previousStoreSelection[0])) {
                    this.transformArticlesToMultiStore(previousStoreSelection[0]);
                } else {
                    this.clear();
                }
            }

            this.insertNewStoresInArticles(_.difference(newStoreSelection, previousStoreSelection));
        }

        if (newStoreSelection.length === 1 && previousStoreSelection.length > 1) {
            this.transformArticlesToSingleStore(newStoreSelection[0]);
        }

        setItemAsJson(this.storageKey, this.listOfItems);
    }

    public hasAtLeastOneBrandWithEnabledSettings() {
        return this.items.pipe(
            map((items) => _.uniq(_.map(items, 'brandId')).filter(Boolean)),
            switchMap((brandIds) => {
                if (!brandIds.length) {
                    return of(false);
                }

                return this.orderService.getBrandOrderSettings(brandIds);
            }),
            catchError(() => of(false)),
            map(
                (brandSettings) =>
                    Array.isArray(brandSettings) &&
                    (brandSettings.some((brandSetting) => brandSetting.acceptsNotes) ||
                        brandSettings.some((brandSetting) => brandSetting.shippingOptions?.length))
            )
        );
    }

    public setSourceStoreIdForCopying(storeId: string) {
        this.sourceStoreIdForCopying.next(storeId);
    }

    public setTargetStoreIdForCopying(storeIds: string[]) {
        this.targetStoreIdsForCopying.next(storeIds);
    }

    public transformArticlesToSingleStore(newStoreId: string) {
        Object.values(this.listOfItems).forEach((item) => {
            item.sizes.forEach((size) => {
                size.stock.selectedAmount = size.stock.selectedAmount[newStoreId];
            });

            item.prepacks.forEach((prepack) => {
                prepack.stock.selectedAmount = prepack.stock.selectedAmount[newStoreId];
            });
        });

        this.changed();
    }

    private transformArticlesToMultiStore(previousStoresId: string) {
        Object.values(this.listOfItems).forEach((item) => {
            item.sizes.forEach((size) => {
                size.stock.selectedAmount = {
                    [previousStoresId]: size.stock.selectedAmount as number
                };
            });
            item.prepacks.forEach((prepack) => {
                prepack.stock.selectedAmount = {
                    [previousStoresId]: prepack.stock.selectedAmount as number
                };
            });
        });
    }

    private insertNewStoresInArticles(storeIds: string[]) {
        const firstStore = storeIds[0];

        Object.values(this.listOfItems).forEach((item) => {
            item.sizes.forEach((size) => {
                storeIds.forEach((storeId) => {
                    if (typeof size.stock.selectedAmount === 'number' && storeId === firstStore) {
                        size.stock.selectedAmount = { [storeId]: size.stock.selectedAmount };
                    } else if (size.stock.selectedAmount) {
                        size.stock.selectedAmount[storeId] = null;
                    } else {
                        size.stock.selectedAmount = { [storeId]: null };
                    }
                });
            });

            item.prepacks.forEach((prepack) => {
                storeIds.forEach((storeId) => {
                    if (typeof prepack.stock.selectedAmount === 'number' && storeId === firstStore) {
                        prepack.stock.selectedAmount[storeId] = { [storeId]: prepack.stock.selectedAmount };
                    } else if (prepack.stock.selectedAmount) {
                        prepack.stock.selectedAmount[storeId] = null;
                    } else {
                        prepack.stock.selectedAmount = { [storeId]: null };
                    }
                });
            });
        });

        this.changed();
    }

    private getMinimumOrderRestriction(restrictionByBrand: BrandsOrderRestriction, brand: string) {
        return _.get(restrictionByBrand, `[${brand}]`);
    }

    private changed(): void {
        setItemAsJson(this.storageKey, this.listOfItems);
        this.items.next(_.values(this.listOfItems));
    }

    private hasValidItemsForOrder(items: ShoppingCartItem[], selectedStores: Branch[]): boolean {
        const sizes = _.flatMap(items, (item) => item.sizes);
        const prepacks = _.reduce(
            items,
            (orderedPrepacks: ReorderPrepack[], { prepacks: currentPrepacks }) => [
                ...orderedPrepacks,
                ...(currentPrepacks ? currentPrepacks : [])
            ],
            []
        );

        const validPrepacks = _.filter(
            prepacks,
            ({ stock }) =>
                getMatrixSelectedAmount(stock.selectedAmount) <= stock.total &&
                getMatrixSelectedAmount(stock.selectedAmount) > 0
        );

        const validSizes = _.filter(
            sizes,
            ({ stock }) =>
                getMatrixSelectedAmount(stock.selectedAmount) <= stock.total &&
                getMatrixSelectedAmount(stock.selectedAmount) > 0
        );

        const invalidRestrictions = _.filter(_.values(_.groupBy(items, 'brandName')), (articles) => {
            const first = articles[0];
            return first.minimumOrderRestriction
                ? this.checkBelowMinimumOrderRestriction(articles, first.minimumOrderRestriction, selectedStores)
                : false;
        });

        return (!_.isEmpty(validSizes) || !_.isEmpty(validPrepacks)) && _.isEmpty(invalidRestrictions);
    }

    private updateOrderIncrement(gtin: string, increment: number): void {
        const item = _.find(this.listOfItems, (currentItem) => _.some(currentItem.sizes, { gtin }));
        const updatedSizes = _.map(item.sizes, (size) => {
            if (size.gtin !== gtin) {
                return size;
            }

            size.orderIncrement = increment;

            return { ...size };
        });
        this.add([{ ...item, sizes: updatedSizes }]);
    }

    private updateAvailableStock(gtin: string, availableStock: number, store: Branch): void {
        const item = _.find(this.listOfItems, (currentItem) => _.some(currentItem.sizes, { gtin }));
        const updatedSizes = _.map(item.sizes, (size) => {
            if (size.gtin !== gtin) {
                return size;
            }

            let selectedAmount: DefaultSelectionType;

            if (typeof size.stock.selectedAmount === 'number') {
                if (availableStock === 0) {
                    selectedAmount = 0;
                    this.removeSelectedSizes([size], store._id);
                } else {
                    selectedAmount = availableStock;
                }
            } else {
                selectedAmount = {
                    ...size.stock.selectedAmount,
                    [store._id]: availableStock !== 0 ? availableStock : size.stock.selectedAmount[store._id]
                };
                item.storeId = store._id;
            }

            return {
                ...size,
                stock: {
                    available: availableStock,
                    total: availableStock,
                    selectedAmount
                }
            };
        });
        this.add([{ ...item, sizes: updatedSizes }]);
    }

    private getItemsToOrder(): OrderedItem[] {
        const filterItemsWithValidAmount = <
            T extends ReorderArticleSize<DefaultSelectionType> | ReorderPrepack<DefaultSelectionType>
        >(
            items: T[]
        ) =>
            _.filter(items, ({ stock }) => {
                if (!stock.selectedAmount) {
                    return false;
                }
                if (typeof stock.selectedAmount === 'number') {
                    return stock.selectedAmount > 0;
                }
                if (typeof stock.selectedAmount === 'object') {
                    return Object.values(stock.selectedAmount).some((amount) => amount > 0);
                }
                return false;
            });
        return _.reduce(
            this.listOfItems,
            (sizesAndPrepacks, { sizes, prepacks }) => [
                ...sizesAndPrepacks,
                ...this.mapToOrderItems(filterItemsWithValidAmount(sizes), OrderedItemType.Product, 'gtin'),
                ...this.mapToOrderItems(filterItemsWithValidAmount(prepacks), OrderedItemType.Prepack, '_id')
            ],
            [] as OrderedItem[]
        );
    }

    private mapToOrderItems<T extends ReorderArticleSize<DefaultSelectionType> | ReorderPrepack<DefaultSelectionType>>(
        itemsToOrder: T[],
        type: OrderedItemType,
        itemIdentifier: keyof T
    ): OrderedItem[] {
        return itemsToOrder.map((item) => ({
            identifier: item[itemIdentifier] as string,
            type,
            quantity: item.stock.selectedAmount
        }));
    }

    private trackOrder(
        orderNumber: { orderNumber: string },
        isMultiStore: boolean,
        isMultiStoreUserWithSingleStoreOrder: boolean
    ) {
        const orderItems = this.getItemsToOrder();
        const itemList = _.values(this.listOfItems);

        const brandsOrderedFrom = _.uniq(itemList.map(({ brandName }) => brandName));
        const brandsIdsOrderedFrom = _.uniq(itemList.map(({ brandId }) => brandId));
        const itemsWithPrices = itemList.filter((orderItem: ReorderArticle) => orderItem.price?.purchasePrice);
        const revenue = _.sumBy(itemsWithPrices, (itemWithPrices: ReorderArticle) =>
            getTotalPriceByItem(itemWithPrices)
        );
        const totalQuantity = _.sum(orderItems.map(({ quantity }) => quantity));

        const articlesWithDiscountedPrices = itemList.filter(
            (orderItem: ReorderArticle) => orderItem.hasDiscountedPurchasePrice
        );
        const articlesWithoutDiscountedPrices = itemList.filter(
            (orderItem: ReorderArticle) => !orderItem.hasDiscountedPurchasePrice
        );

        const articlesTrackingEvent: ArticlesTrackingEvent[] = itemsWithPrices.map((orderItem: ReorderArticle) => {
            const payload: ArticlesTrackingEvent = {
                articleNumber: orderItem.articleNumber,
                $purchasePrice: orderItem.price.purchasePrice
            };
            if (orderItem.hasDiscountedPurchasePrice) {
                payload.$discountedPrice = orderItem.price.discountedPurchasePrice;
            }
            return payload;
        });

        this.segmentService.track('[Re] shoppingcart includes discounted articles', {
            articles: articlesTrackingEvent,
            itemsWithDiscountedPrices: _.size(articlesWithDiscountedPrices),
            itemsWithoutDiscountedPrices: _.size(articlesWithoutDiscountedPrices)
        });

        this.segmentService.track('Reorder submitted', {
            items: orderItems,
            orderUniqueBrand: brandsOrderedFrom,
            orderUniqueBrandId: brandsIdsOrderedFrom,
            orderUniqueBrandCount: _.size(brandsOrderedFrom),
            numberOfItems: _.size(orderItems),
            totalQuantity,
            revenue,
            $revenue: revenue, // `$` prefixes are special Segment properties that pass the values to Segment LTV
            $quantity: totalQuantity,
            $price: _.meanBy(itemsWithPrices, 'price.purchasePrice')
        });

        const orderItemsForEcommEvent = this.getOrderItemsForEcommEventTracking();

        if (isMultiStore) {
            this.segmentService.track(TrackingEvents.MULTI_STORE_ORDER_PLACE, {
                ecommerce: {
                    transaction_id: orderNumber?.orderNumber ?? `${Date.now()}`,
                    value: revenue,
                    currency: _.head(itemsWithPrices)?.price?.currency,
                    items: orderItemsForEcommEvent
                }
            });
        }

        this.segmentService.track(
            isMultiStoreUserWithSingleStoreOrder
                ? TrackingEvents.MULTI_STORE_USER_WITH_SINGLE_STORE_ORDER_PLACE
                : TrackingEvents.PURCHASE,
            {
                ecommerce: {
                    transaction_id: orderNumber?.orderNumber ?? `${Date.now()}`,
                    value: revenue,
                    currency: _.head(itemsWithPrices)?.price?.currency,
                    items: orderItemsForEcommEvent
                }
            }
        );
    }

    private getOrderItemsForEcommEventTracking() {
        const filterItemsWithValidAmount = <
            T extends ReorderArticleSize<DefaultSelectionType> | ReorderPrepack<DefaultSelectionType>
        >(
            items: T[]
        ) =>
            _.filter(items, ({ stock }) => {
                if (typeof stock.selectedAmount === 'number') {
                    return stock.selectedAmount > 0;
                }
                if (stock.selectedAmount && typeof stock.selectedAmount === 'object') {
                    return Object.values(stock.selectedAmount).some((amount) => amount > 0);
                }
                return false;
            });

        return _.reduce(
            this.listOfItems,
            (items, { sizes, color }) => [
                ...items,
                ..._.map(filterItemsWithValidAmount(sizes), (size) => ({
                    item_id: size.gtin,
                    item_name: size.name,
                    affiliation: 'reorder',
                    currency: size.price?.currency,
                    discount: size.price?.discount,
                    item_brand: size.brandName,
                    item_list_id: 'related_products',
                    item_list_name: 'Related Products',
                    item_variant: color?.name,
                    price: size.price?.discountedPurchasePrice || size.price?.purchasePrice,
                    quantity: size.stock.selectedAmount
                }))
            ],
            []
        );
    }

    private trackClearCart() {
        const itemList = _.values(this.listOfItems);
        const itemsWithPrices = itemList.filter((orderItem: ReorderArticle) => orderItem.price?.purchasePrice);
        const revenue = _.sumBy(itemsWithPrices, (itemWithPrices: ReorderArticle) =>
            getTotalPriceByItem(itemWithPrices)
        );
        const orderItemsForEcommEvent = this.getOrderItemsForEcommEventTracking();

        this.segmentService.track(TrackingEvents.REMOVE_FROM_CART, {
            ecommerce: {
                value: revenue,
                items: orderItemsForEcommEvent
            }
        });
    }

    private getBrandsStoreHasNoAccessTo(errors: any[]): string[] {
        const storeWithoutAccessError = _.find(errors, { type: 'StoreWithoutAccess' });
        return storeWithoutAccessError.brands.map(({ name }) => name).join(', ');
    }

    private updateSelectedQuantities(
        article: ShoppingCartItem,
        storeId: string,
        size: ReorderArticleSize | ReorderPrepack,
        isPrepack: boolean
    ) {
        const perStoreQuantity = getStoreSelectedAmount(size.stock.selectedAmount, storeId);

        let aggregatedQuantity: DefaultSelectionType;

        if (this.isSingleStore.getValue()) {
            aggregatedQuantity = perStoreQuantity;
        } else {
            const key = isPrepack ? (size as ReorderPrepack)._id : (size as ReorderArticleSize).gtin;
            const previousQuantity = getItemFromArticleWithKey(article, key)?.stock.selectedAmount;

            aggregatedQuantity = {
                ...(article && previousQuantity && typeof previousQuantity === 'object' ? previousQuantity : {}),
                [storeId]: perStoreQuantity
            };
        }

        return {
            ...size,
            stock: {
                ...size.stock,
                selectedAmount: aggregatedQuantity
            }
        };
    }
}

interface ShoppingCartList {
    [key: string]: ShoppingCartItem;
}

export enum StoreErrors {
    InsufficientQuantity = 'ORDER.SHOPPING_CART.CHECK_QUANTITY',
    Unknown = 'TEXT_UNKNOWN_ERROR',
    StoreHasNoAccessTo = 'ORDER.SHOPPING_CART.STORE_WITHOUT_ACCESS_ERROR',
    InvalidOrderQuantityIncrement = 'ORDER.SHOPPING_CART.INVALID_ORDER_QUANTITY_INCREMENT'
}

export interface ItemsByBrand {
    showsDefaultPricelist: boolean;
    brandName: string;
    items: ShoppingCartItem[];
    minimumOrderRestriction: MinimumOrderRestriction;
    formattedItems: (ShoppingCartItem & {
        options?: {
            showTotals: boolean;
        };
        dataSource: Row[];
        headers: Header[];
        verticalHeaders?: Header[];
    })[];
}

interface StoreErrorMessage extends CartErrors<StoreErrors> {
    message: StoreErrors;
    data: {
        store?: Branch;
        brands?: string[];
        articles?: unknown[];
        error?: unknown;
    };
}

interface ArticlesTrackingEvent {
    articleNumber: string;
    $purchasePrice: number;
    $discountedPrice?: number;
}
